import React, { useState } from 'react'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { getErrorCode } from '../../../../common/api/errors'
import { pauseMatchingPayout, resumeMatchingPayout } from '../../core/_requests'
import styles from './PayoutPauseButton.module.css'

const AppSwal = withReactContent(Swal)

const NOT_IN_MATCHING = 'MATCHING_PAUSE_NOT_IN_MATCHING'

const ERROR_TEXT: Record<string, string> = {
  [NOT_IN_MATCHING]: 'Выплата уже не в мэтчинге.',
  MATCHING_PAUSE_UNAVAILABLE: 'Мэтчинг не ответил: состояние выплаты не изменено. Повторите действие.',
}

interface Props {
  token: string
  paused: boolean
  /** Состояние выплаты изменилось или оказалось не тем, что на экране: список перечитывается. */
  onChanged: () => void
}

/**
 * Пауза и возврат в работу выплаты, которая находится в мэтчинге.
 *
 * Выплата на паузе остаётся в мэтчинге: новые пополнения на неё не назначаются, уже назначенные
 * оплачиваются как обычно. Действие обратимое и без окна подтверждения — как у магнитофона. Пока
 * выплата на паузе, по контуру кнопки бежит «змейка».
 */
export const PayoutPauseButton: React.FC<Props> = ({ token, paused, onChanged }) => {
  const [submitting, setSubmitting] = useState(false)

  const toggle = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      await (paused ? resumeMatchingPayout(token) : pauseMatchingPayout(token))
      onChanged()
    } catch (error) {
      const code = getErrorCode(error)
      // Модалка в catch заменяет общий алерт перехватчика в том же тике.
      AppSwal.fire(
        paused ? 'Выплата не возвращена в работу' : 'Выплата не приостановлена',
        (code && ERROR_TEXT[code]) || 'Не удалось изменить состояние выплаты. Повторите действие.',
        'warning'
      )
      // Строка на экране устарела: выплата уже вышла из мэтчинга.
      if (code === NOT_IN_MATCHING) onChanged()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <button
      type='button'
      className={clsx(
        'btn btn-icon btn-bg-light btn-active-color-primary btn-sm',
        paused && !submitting && styles.snake
      )}
      onClick={toggle}
      disabled={submitting}
      title={paused ? 'Вернуть в работу' : 'Приостановить'}
    >
      {submitting ? (
        <span className='spinner-border spinner-border-sm text-warning' />
      ) : (
        <i className={clsx('bi fs-2', paused ? 'bi-play-fill text-success' : 'bi-pause-fill text-warning')} />
      )}
    </button>
  )
}

export default PayoutPauseButton
